import React from 'react';
import styles from './WidgetCardHeader.module.scss';
import {
  SFChip,
  SFDivider,
  SFIcon,
  SFIconButton,
  SFMenu,
  SFMenuItem,
  SFTooltip,
  SFBlueMainLight,
  SFBlueMainDark
} from 'sfui';
import { TextOverflowTooltip } from '../../TextOverflowTooltip/TextOverflowTooltip';
import {
  AppearanceContext,
  InteractiveBox,
  MediaContext
} from 'ui-smartforce-settings';
import { isMobile } from '../../../Helpers';
import { WidgetSourceTypes } from '../../../Models';

export interface WidgetCardHeaderMenuOption {
  label: string;
  onClick: () => void;
}

export interface WidgetCardHeaderProps {
  title: string;
  sources?: WidgetSourceTypes[];
  isActive?: boolean;
  drawerMenuOption?: WidgetCardHeaderMenuOption;
  infoMessage?: string;
  isMaximize?: boolean;
  onToggleMaximize?: (isMaximize: boolean) => void;
  onAdd?: () => void;
  onRemove?: () => void;
  onShare?: () => void;
}

export const WidgetCardHeader = ({
  title,
  sources = [],
  isActive = false,
  drawerMenuOption,
  infoMessage,
  isMaximize,
  onToggleMaximize,
  onAdd,
  onRemove,
  onShare
}: WidgetCardHeaderProps): React.ReactElement<WidgetCardHeaderProps> => {
  const { isPhone } = React.useContext(MediaContext);
  const { themeType } = React.useContext(AppearanceContext);
  const [isMenuOpen, setIsMenuOpen] = React.useState<boolean>(false);
  const [anchorEl, setAnchorEl] = React.useState<any>();
  const [isTooltipOpen, setIsTooltipOpen] = React.useState<boolean>(false);

  const onInfoClose = () => {
    if (isMobile()) {
      setIsTooltipOpen(false);
    }
  };

  const onInfoClick = () => {
    if (isMobile() && !isTooltipOpen) {
      setIsTooltipOpen(true);
    }
  };

  const onClick = (e: any) => {
    if (!isMenuOpen) {
      setAnchorEl(e.currentTarget);
      setIsMenuOpen(true);
    }
  };

  const displayInfoIcon = !!infoMessage || sources.length > 0;

  const sourceChipsContent = (
    sources: WidgetSourceTypes[],
    inTooltip: boolean,
    hasMessage: boolean
  ) => {
    const color =
      themeType === 'day'
        ? inTooltip
          ? SFBlueMainDark
          : 'primary'
        : inTooltip
          ? SFBlueMainLight
          : 'primary';

    return (
      <div
        className={`${styles.sourceChips} ${hasMessage ? styles.divider : ''}`}
      >
        {sources.map((source, index) => (
          <SFChip
            key={`source-${source}-${index}`}
            sfColor={color}
            variant="outlined"
            size="small"
            label={source}
            style={{
              color: color,
              backgroundColor: 'transparent',
              borderColor: color
            }}
          />
        ))}
      </div>
    );
  };

  return (
    <div className={styles.widgetCardHeader}>
      <div className={styles.title}>
        <TextOverflowTooltip title={title}>
          <span className={styles.h3}>{title}</span>
        </TextOverflowTooltip>

        {displayInfoIcon && (
          <SFTooltip
            classes={{
              popper: styles.limitedResponseTooltip
            }}
            title={infoMessage || ''}
            placement={isPhone ? 'bottom' : 'bottom-start'}
            open={isMobile() ? isTooltipOpen : undefined}
            leaveTouchDelay={3500}
            onClose={onInfoClose}
            disableHoverListener={isMobile()}
            PopperProps={{ disablePortal: true, style: { zIndex: 1199 } }}
            content={
              sources.length > 0
                ? sourceChipsContent(sources, true, !!infoMessage)
                : undefined
            }
          >
            <InteractiveBox className={styles.icon} onClick={onInfoClick}>
              <SFIcon icon="Information" size={14} />
            </InteractiveBox>
          </SFTooltip>
        )}
        {sources.length > 0 && (
          <div className={styles.hiddenSourcesWrapper}>
            {sourceChipsContent(sources, false, false)}
          </div>
        )}
      </div>

      <div className={styles.actions}>
        {!isMaximize && onToggleMaximize && (
          <SFIconButton
            aria-label="Maximize"
            sfIcon="Maximize-1"
            sfSize="small"
            onClick={() => onToggleMaximize(true)}
          />
        )}

        {isMaximize && onToggleMaximize && (
          <SFIconButton
            aria-label="Minimize"
            sfIcon="Minimize-1"
            sfSize="small"
            onClick={() => onToggleMaximize(false)}
          />
        )}

        <SFIconButton
          sfIcon="Other"
          sfSize="small"
          rotate="right"
          onClick={onClick}
          aria-label="More Options"
        />
      </div>

      <SFMenu
        MenuListProps={{ className: styles.menu }}
        open={isMenuOpen}
        anchorEl={anchorEl}
        getContentAnchorEl={null}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        onClose={() => setIsMenuOpen(false)}
      >
        {drawerMenuOption && (
          <SFMenuItem
            onClick={() => {
              drawerMenuOption.onClick();
              setIsMenuOpen(false);
            }}
          >
            {drawerMenuOption.label}
          </SFMenuItem>
        )}

        <SFMenuItem
          disabled={!onShare}
          className={styles.shareItem}
          onClick={() => {
            setIsMenuOpen(false);
            onShare && onShare();
          }}
        >
          Download
          <SFChip sfColor="default" label="BETA" />
        </SFMenuItem>

        <SFDivider />

        {!isActive && <SFMenuItem onClick={onAdd}>Add</SFMenuItem>}
        {isActive && <SFMenuItem onClick={onRemove}>Remove</SFMenuItem>}
      </SFMenu>
    </div>
  );
};
